import React from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';

/**
 * BulkActionBar — appears when one or more items are selected.
 *
 *   Desktop (sm+): floats at the top of the page list, sticky.
 *   Mobile (<sm):  fixed at the bottom of the viewport with safe-area padding.
 *
 * Props:
 *   count        — number of items currently selected
 *   onClear      — clears selection
 *   children     — action buttons (use <Button> variants)
 *   itemLabel    — singular label of items ("produk", "paket")
 *   className    — additional classes
 *   testid       — root data-testid (defaults to "bulk-action-bar")
 */
export const BulkActionBar = ({ count, onClear, children, itemLabel = 'item', className = '', testid = 'bulk-action-bar' }) => {
  if (!count) return null;
  return (
    <>
      {/* Mobile bottom-sticky bar */}
      <div
        data-testid={`${testid}-mobile`}
        className={`md:hidden fixed left-0 right-0 bottom-0 z-40 border-t border-border bg-card/95 backdrop-blur shadow-[0_-8px_24px_rgba(0,0,0,0.35)] px-4 pt-3 pb-[calc(env(safe-area-inset-bottom,0)+12px)] ${className}`}
      >
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-sm font-semibold flex items-center gap-2">
            <button
              type="button"
              onClick={onClear}
              aria-label="Batalkan pilihan"
              className="h-8 w-8 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5"
            >
              <X className="h-4 w-4" />
            </button>
            <span>{count} {itemLabel} dipilih</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {children}
        </div>
      </div>

      {/* Desktop top sticky bar */}
      <div
        data-testid={`${testid}-desktop`}
        className={`hidden md:flex sticky top-0 z-30 items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 backdrop-blur px-4 py-2.5 shadow-sm flex-wrap ${className}`}
      >
        <button
          type="button"
          onClick={onClear}
          aria-label="Batalkan pilihan"
          className="h-7 w-7 rounded-md inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/5"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold mr-2">{count} {itemLabel} dipilih</div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {children}
        </div>
      </div>
    </>
  );
};

// Small button variant tuned for the bulk action bar.
export const BulkButton = React.forwardRef(({ children, className = '', ...rest }, ref) => (
  <Button
    ref={ref}
    type="button"
    size="sm"
    variant="secondary"
    className={`h-10 md:h-8 justify-center ${className}`}
    {...rest}
  >
    {children}
  </Button>
));
BulkButton.displayName = 'BulkButton';
